/**
 * AutocompleteEmpty — React (web) implementation.
 *
 * A static card; it has no controls of its own. An inline link inside the
 * body is composed by the host as children (e.g. a `Link` with a `mailto:`).
 */

import React, { type ReactNode } from 'react';
import {
  AUTOCOMPLETE_EMPTY_BACKGROUND,
  AUTOCOMPLETE_EMPTY_BODY_COLOR,
  AUTOCOMPLETE_EMPTY_BODY_TYPOGRAPHY,
  AUTOCOMPLETE_EMPTY_BORDER_COLOR,
  AUTOCOMPLETE_EMPTY_BORDER_WIDTH,
  AUTOCOMPLETE_EMPTY_GAP,
  AUTOCOMPLETE_EMPTY_PADDING_HORIZONTAL,
  AUTOCOMPLETE_EMPTY_PADDING_VERTICAL,
  AUTOCOMPLETE_EMPTY_RADIUS,
  AUTOCOMPLETE_EMPTY_SHADOW,
  AUTOCOMPLETE_EMPTY_TITLE_COLOR,
  AUTOCOMPLETE_EMPTY_TITLE_TYPOGRAPHY,
  type AutocompleteEmptyProps,
} from './AutocompleteEmpty';

export interface WebAutocompleteEmptyProps extends AutocompleteEmptyProps {
  /** Rich body content; replaces `body` when given. */
  children?: ReactNode;
  'data-testid'?: string;
}

const SHADOW = `${AUTOCOMPLETE_EMPTY_SHADOW.offset.width}px ${AUTOCOMPLETE_EMPTY_SHADOW.offset.height}px ${AUTOCOMPLETE_EMPTY_SHADOW.radius}px ${AUTOCOMPLETE_EMPTY_SHADOW.spread}px ${AUTOCOMPLETE_EMPTY_SHADOW.color}`;

export function AutocompleteEmpty({
  title,
  body,
  testID,
  children,
  'data-testid': dataTestId,
}: WebAutocompleteEmptyProps) {
  const content = children ?? body;
  if (!title && !content) return null;
  const testId = dataTestId ?? testID;

  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: AUTOCOMPLETE_EMPTY_GAP,
        padding: `${AUTOCOMPLETE_EMPTY_PADDING_VERTICAL}px ${AUTOCOMPLETE_EMPTY_PADDING_HORIZONTAL}px`,
        backgroundColor: AUTOCOMPLETE_EMPTY_BACKGROUND,
        border: `${AUTOCOMPLETE_EMPTY_BORDER_WIDTH}px solid ${AUTOCOMPLETE_EMPTY_BORDER_COLOR}`,
        borderRadius: AUTOCOMPLETE_EMPTY_RADIUS,
        boxShadow: SHADOW,
      }}
    >
      {title ? (
        <p
          data-testid={testId ? `${testId}-title` : undefined}
          style={{
            margin: 0,
            color: AUTOCOMPLETE_EMPTY_TITLE_COLOR,
            fontFamily: AUTOCOMPLETE_EMPTY_TITLE_TYPOGRAPHY.fontFamily,
            fontWeight: AUTOCOMPLETE_EMPTY_TITLE_TYPOGRAPHY.fontWeight,
            fontSize: AUTOCOMPLETE_EMPTY_TITLE_TYPOGRAPHY.fontSize,
            lineHeight: `${AUTOCOMPLETE_EMPTY_TITLE_TYPOGRAPHY.lineHeight}px`,
            letterSpacing: AUTOCOMPLETE_EMPTY_TITLE_TYPOGRAPHY.letterSpacing,
          }}
        >
          {title}
        </p>
      ) : null}
      {content ? (
        // A div, not a p: composed children may carry block content.
        <div
          data-testid={testId ? `${testId}-body` : undefined}
          style={{
            margin: 0,
            color: AUTOCOMPLETE_EMPTY_BODY_COLOR,
            fontFamily: AUTOCOMPLETE_EMPTY_BODY_TYPOGRAPHY.fontFamily,
            fontWeight: AUTOCOMPLETE_EMPTY_BODY_TYPOGRAPHY.fontWeight,
            fontSize: AUTOCOMPLETE_EMPTY_BODY_TYPOGRAPHY.fontSize,
            lineHeight: `${AUTOCOMPLETE_EMPTY_BODY_TYPOGRAPHY.lineHeight}px`,
            letterSpacing: AUTOCOMPLETE_EMPTY_BODY_TYPOGRAPHY.letterSpacing,
          }}
        >
          {content}
        </div>
      ) : null}
    </div>
  );
}
